import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { DashboardWindow } from "./dashboardHelpers";
import { useIsMobile } from "./useIsMobile";

interface SelectOption {
  value: string;
  label: string;
}

// Below this many options a search box is noise (the whole list is visible at a glance);
// from here up it earns its place. Mirrored in MultiSelectSlicer.
const SEARCH_THRESHOLD = 8;

export interface SingleSelectSlicerProps {
  id: string;
  name: string;
  label: string;
  options: SelectOption[];
  value: string;
  onChange: (value: string) => void;
  targetWindow: DashboardWindow;
}

/**
 * Desktop: a combobox — trigger button plus a popover with a type-ahead search box over the
 * options. Mobile: the same inline expandable card MultiSelectSlicer renders, with radio rows
 * instead of checkboxes, so the two option filters behave alike in the Filters sheet.
 *
 * Both presentations show `label` alongside the selected option, as MultiSelectSlicer does —
 * without it a row of boolean filters is a row of identical chips reading "All" (ENG-8482).
 */
export const SingleSelectSlicer: React.FC<SingleSelectSlicerProps> = ({
  id,
  label,
  options,
  value,
  onChange,
  targetWindow,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [buttonRect, setButtonRect] = useState<DOMRect | null>(null);
  const [search, setSearch] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile(targetWindow);

  const selectedLabel = useMemo(
    () => options.find(o => o.value === value)?.label ?? value,
    [options, value]
  );

  const showSearch = options.length >= SEARCH_THRESHOLD;
  const filtered = useMemo(
    () =>
      search
        ? options.filter(o =>
            String(o.label ?? o.value)
              .toLowerCase()
              .includes(search.toLowerCase())
          )
        : options,
    [options, search]
  );

  const openPopover = useCallback(() => {
    if (buttonRef.current) {
      setButtonRect(buttonRef.current.getBoundingClientRect());
    }
    setSearch("");
    setHighlightedIndex(-1);
    setIsOpen(true);
  }, []);

  const closePopover = useCallback(() => {
    setIsOpen(false);
    setSearch("");
    setHighlightedIndex(-1);
  }, []);

  const commit = useCallback(
    (optionValue: string) => {
      onChange(optionValue);
      closePopover();
      buttonRef.current?.focus();
    },
    [onChange, closePopover]
  );

  // Close on outside click, Escape, scroll, or resize — the popover is fixed-positioned
  // against a snapshot of the trigger rect, so it goes stale when the page moves. Desktop only:
  // on mobile this renders as an expandable section inline in the Filters sheet, not a floating
  // popover, so an outside tap or a scroll must leave the expanded section alone.
  useEffect(() => {
    if (!isOpen || isMobile) {
      return;
    }
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (popoverRef.current?.contains(target) || buttonRef.current?.contains(target)) {
        return;
      }
      closePopover();
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closePopover();
        buttonRef.current?.focus();
      }
    };
    const handleScroll = (e: Event) => {
      if (popoverRef.current?.contains(e.target as Node)) {
        return;
      }
      closePopover();
    };
    targetWindow.document.addEventListener("mousedown", handleClickOutside);
    targetWindow.document.addEventListener("keydown", handleEscape);
    targetWindow.addEventListener("scroll", handleScroll, true);
    targetWindow.addEventListener("resize", closePopover);
    return () => {
      targetWindow.document.removeEventListener("mousedown", handleClickOutside);
      targetWindow.document.removeEventListener("keydown", handleEscape);
      targetWindow.removeEventListener("scroll", handleScroll, true);
      targetWindow.removeEventListener("resize", closePopover);
    };
  }, [isOpen, targetWindow, closePopover, isMobile]);

  // Reset the highlight when the result set changes; keep it scrolled into view otherwise.
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [search]);
  useEffect(() => {
    if (highlightedIndex < 0 || !listRef.current) {
      return;
    }
    const item = listRef.current.children[highlightedIndex] as HTMLElement | undefined;
    item?.scrollIntoView({ block: "nearest" });
  }, [highlightedIndex]);

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (filtered.length === 0) {
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex(i => (i + 1) % filtered.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex(i => (i - 1 + filtered.length) % filtered.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      // No highlight yet -> first match, the muscle-memory "type then Enter" path.
      const target = highlightedIndex >= 0 ? filtered[highlightedIndex] : filtered[0];
      commit(target.value);
    }
  };

  // Mobile (<640px), inside the Filters sheet: the same inline expandable card MultiSelectSlicer
  // renders (Figma "Filters Closed") — collapsed it is name left, current value muted right,
  // chevron; expanded it reveals the options below a divider. Nothing is portalled or
  // fixed-positioned, so the sheet just grows and scrolls. Sizes are the Figma's exact px rather
  // than the rem scale: the viewer renders in a 14px-root iframe, so `text-sm`/`p-2` would land
  // ~12.5% under spec (12.25px text, a 14px control).
  if (isMobile) {
    const listId = `${id}_options`;
    const optionRowClassName =
      "flex items-center gap-[8px] p-[8px] text-[14px] leading-[18px] font-semibold text-main-foreground cursor-pointer";
    // No rounded-[2px] here, unlike the multi-select's checkbox: a native radio is already round.
    const radioClassName = "size-[16px] shrink-0 accent-primary-button-background cursor-pointer";
    return (
      <div
        className={`w-full rounded-[8px] bg-main-background-secondary px-[12px] ${
          // Collapsed the card itself supplies the 12px above/below the single row; expanded it
          // drops to 8px because the title row and the last option row each add their own 8px.
          isOpen ? "py-[8px]" : "py-[12px]"
        }`}
      >
        <div className="flex flex-col gap-[4px]">
          {/* The focus ring is one deliberate departure from MultiSelectSlicer's mobile trigger,
              which has none: the card this replaces carried one via `peer-focus-visible`, and
              dropping it would regress keyboard focus visibility. */}
          <button
            id={id}
            ref={buttonRef}
            type="button"
            onClick={() => setIsOpen(v => !v)}
            aria-expanded={isOpen}
            aria-controls={listId}
            className={`flex w-full items-center gap-[12px] text-left cursor-pointer rounded-[4px] focus-visible:ring-2 focus-visible:ring-primary-button-background ${
              // The divider separates the title from the option rows, so it is expanded-only.
              isOpen ? "border-b border-main-border py-[8px] pr-[8px]" : ""
            }`}
          >
            <span className="flex-1 min-w-0 text-[14px] leading-[18px] font-bold text-main-foreground truncate">
              {label}
            </span>
            {!isOpen && (
              <span className="flex-1 min-w-0 text-[14px] leading-[18px] font-semibold text-main-muted-foreground text-right truncate">
                {selectedLabel}
              </span>
            )}
            <span
              className={`material-icons !text-[16px] shrink-0 text-main-foreground transition-transform duration-150 ${
                isOpen ? "rotate-180" : ""
              }`}
            >
              expand_more
            </span>
          </button>
          {isOpen && (
            // `radiogroup`, not the multi-select's plain `group`: one choice, not many. Options
            // come from `options` rather than `filtered` because the search box is desktop-only,
            // so `search` is never set here — same as MultiSelectSlicer's inline section.
            <div
              id={listId}
              role="radiogroup"
              aria-label={label}
              className="flex flex-col gap-[4px] min-w-0"
            >
              {options.length === 0 ? (
                <div className="p-[8px] text-[14px] leading-[18px] text-main-muted-foreground">
                  No options
                </div>
              ) : (
                options.map(option => (
                  <label key={option.value} className={optionRowClassName}>
                    <input
                      type="radio"
                      name={id}
                      checked={option.value === value}
                      // Picking one ends the interaction, as the platform picker it replaces did.
                      // The multi-select stays open because it expects several taps.
                      onChange={() => {
                        onChange(option.value);
                        setIsOpen(false);
                      }}
                      className={radioClassName}
                    />
                    <span className="min-w-0 break-words">{option.label}</span>
                  </label>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  const popover =
    isOpen && buttonRect ? (
      <div
        ref={popoverRef}
        role="listbox"
        style={{
          position: "fixed",
          top: buttonRect.bottom + 4,
          left: buttonRect.left,
          // Explicit width (not minWidth): a shrink-to-fit popover full of w-full children can
          // stretch to the whole viewport inside the themed iframe. The dropdown hugs its
          // trigger like a native select; long labels truncate.
          width: Math.max(220, buttonRect.width),
          zIndex: 9999,
        }}
        className="rounded-themed border border-card-border bg-card-background text-card-foreground shadow-card overflow-hidden"
      >
        {showSearch && (
          <div className="p-2 border-b border-card-border">
            <input
              autoFocus
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={handleSearchKeyDown}
              placeholder="Search..."
              aria-label="Search options"
              className="w-full px-2 py-1.5 text-sm bg-card-background border border-card-border rounded-themed outline-none text-card-foreground placeholder:text-card-foreground/50"
            />
          </div>
        )}
        <div ref={listRef} className="max-h-72 overflow-y-auto themed-menu-scrollbar py-1">
          {filtered.length === 0 ? (
            <div className="px-4 py-2 text-sm text-card-foreground/50">No matches</div>
          ) : (
            filtered.map((option, i) => (
              <button
                key={option.value}
                type="button"
                role="option"
                aria-selected={option.value === value}
                onClick={() => commit(option.value)}
                className={`w-full text-left px-4 py-1.5 text-sm cursor-pointer ${
                  i === highlightedIndex ? "bg-card-foreground/5" : "hover:bg-card-foreground/5"
                } ${option.value === value ? "font-semibold text-primary-button-background" : ""}`}
              >
                <span className="block truncate">{option.label}</span>
              </button>
            ))
          )}
        </div>
      </div>
    ) : null;

  return (
    <div className="relative inline-flex items-center">
      <button
        id={id}
        ref={buttonRef}
        type="button"
        onClick={() => (isOpen ? closePopover() : openPopover())}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        className="themed-slicer-input pr-9 text-left"
      >
        {/* The label is guarded, unlike MultiSelectSlicer's unconditional `{label}: {displayText}`:
            `label` is required by the schema, but SlicersSection still defends with
            `slicer.label ?? slicer.name`, so a legacy spec with a blank one must not render ": All". */}
        <span className="truncate">{label ? `${label}: ${selectedLabel}` : selectedLabel}</span>
      </button>
      <span className="absolute right-3 text-card-foreground/70 pointer-events-none">
        <span className="material-icons !text-xl">expand_more</span>
      </span>
      {popover && createPortal(popover, targetWindow.document.body)}
    </div>
  );
};
